import { Chart } from 'chart.js';

/**
 * Resolve a theme CSS custom property to a concrete `hsl()` string for Chart.js.
 *
 * Chart.js paints to a canvas, which cannot read CSS custom properties — it needs
 * a literal colour. Charts therefore historically hardcoded the LIGHT palette
 * (e.g. gridlines `hsl(220 13% 91%)` = `--border` light), which glared as bright
 * white lines on the dark canvas. Reading the value live at options-build time
 * means a chart constructed after the no-flash theme script picks up the active
 * theme's value (light or dark) automatically, with the original literal as a
 * safe fallback.
 *
 * Mirrors the `cssHsl()` helper already used in `calio-pie-chart`.
 */
export function chartThemeColor(cssVar: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return value ? `hsl(${value})` : fallback;
}

/**
 * Push the active theme's colours into Chart.js GLOBAL defaults and re-render every live
 * chart. Chart.js paints to a canvas (can't read CSS custom properties) and its built-in
 * default text colour is `#666` — invisible on the dark canvas — so any legend label,
 * axis tick or gridline that does NOT set an explicit colour disappears in dark mode.
 * Setting the globals (the same idiom the charts already use for
 * `Chart.defaults.font.family`) themes every chart at once, and re-rendering the live
 * instances makes a runtime theme toggle take effect immediately instead of only on the
 * next chart build. Called from `ColorSchemeService.applyTheme()` on boot and on every
 * theme change — chart.js is already eager via `provideCharts(...)`, so importing it here
 * adds no bundle weight.
 */
export function applyChartThemeDefaults(): void {
  if (typeof document === 'undefined') { return; }
  Chart.defaults.color = chartThemeColor('--muted-foreground', 'hsl(220, 9%, 46%)');
  Chart.defaults.borderColor = chartThemeColor('--border', 'hsl(220, 13%, 91%)');
  Object.values(Chart.instances).forEach(chart => chart.update('none'));
}
